import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_BASE_LINE_THICKNESS, EDITOR_SHAPE_KEYS } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import useShape from '@editor/hooks/Shape'
import { CylinderMeshProps } from '@editor/interfaces/interfaces'
import { getCenterPoint } from '@editor/services/Editor'
import { CYLINDER_EULER, createCylinderGeometry } from '@editor/services/Shape'
import { useToolUtilSelector } from '@editor/store/selectors'
import { merge } from 'lodash'
import { BackSide, Matrix4, Mesh, MeshBasicMaterial } from 'three'

import Label from './Label'

export const CylinderMesh: FC<CylinderMeshProps> = ({
  id,
  cylinder,
  color = 'red',
  outline,
  label,
  visible = true,
  opacity = 1,
  isAdjustingColors,
}) => {
  // Context
  const { cameraRef } = useContext(EditorContext)

  // States
  const [cylinderMesh, setCylinderMesh] = useState<Mesh>(new Mesh())
  const currentFillColor = useToolUtilSelector((state) => state.currentFillColor)

  // Hooks
  const { onPointerDown, onPointerUp, onPointerOver, onPointerOut } = useShape(
    cylinder,
    EDITOR_SHAPE_KEYS.CYLINDERS,
    cameraRef,
  )

  // Refs
  const meshRef = useRef<Mesh | null>(null)

  // Memoized values
  const outlineConfig = useMemo(() => {
    if (!outline) return null
    return merge(
      {
        color: 'white',
        thickness: 0.005,
      },
      outline,
    )
  }, [outline])

  const labelConfig = useMemo(() => {
    if (!label) return null
    return merge(
      {
        visible: true,
        backgroundColor: 'yellow',
      },
      label,
    )
  }, [label])

  const outlineThickness = EDITOR_BASE_LINE_THICKNESS

  /**
   * Draw cylinder mesh
   */
  useEffect(() => {
    const { diameter, length, transformation } = cylinder
    if (transformation?.length !== 16) {
      return
    }
    const matrix = new Matrix4().set(...transformation)

    //* Rotate the X-axis by 90° in the direction of the Z-axis and generate a mesh with rebar rotation applied.
    const mesh = new Mesh()
    mesh.quaternion.setFromEuler(CYLINDER_EULER)
    mesh.applyMatrix4(matrix)

    //* Setting the shape of the cylinder
    const geo = createCylinderGeometry(diameter, length)
    geo.translate(0, cylinder.length / 2, 0)
    mesh.geometry = geo

    //* state保存
    setCylinderMesh(mesh)
  }, [cylinder])

  /**
   * Calculate cylinder geometry for outline effect
   */
  const outlineGeo = useMemo(() => {
    if (!outlineConfig || outlineThickness <= 0) return null
    if (!outlineConfig) return null

    // used for outline effect
    const { diameter, length } = cylinder
    const geo = createCylinderGeometry(diameter + outlineThickness, length + outlineThickness)
    geo.translate(0, length / 2, 0)

    return geo
  }, [cylinder, outlineThickness, outlineConfig])

  /**
   * Update the mesh material color when the current fill color changes.
   * This is used for real-time updates of the fill color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the `color` prop.
   */
  useEffect(() => {
    if (
      !meshRef.current ||
      !(meshRef.current.material instanceof MeshBasicMaterial) ||
      !currentFillColor ||
      !isAdjustingColors
    )
      return

    meshRef.current.visible = currentFillColor !== 'transparent'
    meshRef.current.material.color.set(currentFillColor)
  }, [currentFillColor, id, isAdjustingColors])

  if (!cylinder) return null

  return (
    <>
      <mesh
        ref={meshRef}
        onPointerUp={onPointerUp}
        onPointerDown={onPointerDown}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        quaternion={cylinderMesh.quaternion}
        position={cylinderMesh.position}
        geometry={cylinderMesh.geometry}
        visible={visible && color !== 'transparent'}
      >
        {/* Use white when set as transparent since 'transparent' is not a valid color */}
        <meshBasicMaterial color={color === 'transparent' ? 'white' : color} opacity={opacity} transparent />

        {labelConfig ? (
          <Label
            id={`${id}-label`}
            point={getCenterPoint(cylinderMesh).toArray()}
            label={labelConfig.label}
            labelPrefix=""
            visible={labelConfig.visible}
            styles={labelConfig.styles}
            isAdjustingColors={labelConfig.isAdjustingColors}
          />
        ) : null}
      </mesh>

      {outlineGeo && outlineConfig ? (
        <mesh
          quaternion={cylinderMesh.quaternion}
          position={cylinderMesh.position}
          geometry={outlineGeo}
          visible={visible}
          renderOrder={5}
        >
          <meshBasicMaterial color={outlineConfig.color} side={BackSide} opacity={opacity} transparent />
        </mesh>
      ) : null}
    </>
  )
}
